import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '';

export default function VertragUnterschreibenPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);

  // Canvas state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Load contract info
  useEffect(() => {
    fetch(`${API}/api/public/vertrag/${token}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 410 ? 'Link abgelaufen' : 'Vertrag nicht gefunden');
        return r.json();
      })
      .then(data => {
        setInfo(data);
        if (data.bereits_unterschrieben) setDone(true);
        setPdfUrl(`${API}/api/public/vertrag/${token}/pdf`);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || done) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1A1A2E';
    ctx.lineWidth = 2.5;
  }, [info, done]);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  }, [isDrawing, getPos]);

  const stopDraw = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasSignature(false);
  };

  const submitSignature = async () => {
    if (!hasSignature || !agreed) return;
    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      const res = await fetch(`${API}/api/public/vertrag/${token}/unterschreiben`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unterschrift: dataUrl, zustimmung: true }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Fehler beim Unterschreiben');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-3 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-2">Fehler</h2>
          <p className="text-[#6B7280]">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-xl font-bold text-[#1A1A2E] mb-2">Vertrag unterschrieben</h2>
          <p className="text-[#6B7280] mb-4">Dein Arbeitsvertrag wurde erfolgreich digital unterschrieben. Du erhältst eine Bestätigung per E-Mail.</p>
          <p className="text-sm text-[#9CA3AF]">Du kannst dieses Fenster jetzt schließen.</p>
        </div>
      </div>
    );
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="min-h-screen bg-gray-50" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#338edc] to-purple-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-extrabold text-xs">W</span>
          </div>
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-[#1A1A2E]">WeShip — Arbeitsvertrag</h1>
            <p className="text-xs text-[#6B7280] truncate">{info?.template_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Name + PDF link */}
        <div className="bg-white rounded-xl border p-4">
          <p className="text-sm text-[#6B7280]">Vertrag für</p>
          <p className="text-lg font-semibold text-[#1A1A2E]">{info?.vorname} {info?.nachname}</p>
          {info?.template_name && (
            <p className="text-sm text-[#6B7280] mt-1">{info.template_name}</p>
          )}
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border border-[#338edc] text-[#338edc] text-sm font-medium hover:bg-blue-50 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Vertrag als PDF öffnen
            </a>
          )}
        </div>

        {/* PDF Viewer — only on desktop */}
        {!isMobile && pdfUrl && (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="p-3 border-b bg-gray-50">
              <p className="text-sm font-medium text-[#1A1A2E]">Vertragsdokument</p>
            </div>
            <iframe
              src={pdfUrl}
              className="w-full border-0"
              style={{ height: '60vh', minHeight: 400 }}
              title="Vertrag PDF"
            />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Agreement checkbox */}
        <label className="flex items-start gap-3 bg-white rounded-xl border p-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            className="mt-0.5 w-5 h-5 rounded border-[var(--border-default,#E5E7EB)] text-[#338edc] focus:ring-[#338edc]"
          />
          <span className="text-sm text-[#4B5563] leading-relaxed">
            Ich habe den Vertrag vollständig gelesen und akzeptiere die darin enthaltenen Bedingungen. Mit meiner digitalen Unterschrift bestätige ich die Richtigkeit meiner Angaben.
          </span>
        </label>

        {/* Signature pad */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <p className="text-sm font-medium text-[#1A1A2E]">Unterschrift</p>
            {hasSignature && (
              <button onClick={clearCanvas} className="text-xs text-[#338edc] hover:underline">
                Löschen
              </button>
            )}
          </div>
          <div className="p-3">
            <canvas
              ref={canvasRef}
              className="w-full border border-dashed border-[var(--border-default,#E5E7EB)] rounded-lg bg-white cursor-crosshair touch-none"
              style={{ height: 160 }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
            <p className="text-xs text-[#9CA3AF] mt-2 text-center">
              Hier mit dem Finger oder der Maus unterschreiben
            </p>
          </div>
        </div>

        {/* Submit button */}
        <button
          onClick={submitSignature}
          disabled={!hasSignature || !agreed || signing}
          className="w-full py-3.5 rounded-xl font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: hasSignature && agreed ? '#338edc' : '#9CA3AF' }}
        >
          {signing ? (
            <span className="flex items-center justify-center gap-2">
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              Wird verarbeitet...
            </span>
          ) : 'Vertrag unterschreiben'}
        </button>

        <p className="text-center text-xs text-[#9CA3AF] pb-6">
          Powered by WeShip · Digitale Vertragsunterschrift
        </p>
      </div>
    </div>
  );
}